import toast from 'react-hot-toast';
import { createElement } from 'react';
import { Toast, ToastVariant } from '../components/ui/Toast';
import { getErrorMessage } from './api/client';

/**
 * How long a toast stays up. Errors linger because the user has to read and act
 * on them; a success is a confirmation they already expect.
 */
const DURATION: Record<ToastVariant, number> = {
  success: 3000,
  info: 4000,
  error: 6000,
};

function show(
  variant: ToastVariant,
  message: string,
  id?: string,
  action?: { label: string; onClick: () => void },
) {
  return toast.custom((t) => createElement(Toast, { t, variant, message, action }), {
    duration: action ? UNDO_DURATION : DURATION[variant],
    id,
  });
}

/** Long enough to notice and reach, short enough not to linger. */
const UNDO_DURATION = 8000;

/**
 * The app's toast API. Use it for the outcome of something the user did —
 * saving, deleting, connecting. Failures to *load* a page or panel belong
 * inline (`ErrorMessage`), and input problems belong on the field itself;
 * see the error-handling rules in docs/product_website/ui-design.md.
 *
 * `showToast.error` takes the caught error rather than a string on purpose:
 * routing every failure through `getErrorMessage` is what keeps a raw exception
 * message — or a backend string written for us rather than for the user — from
 * reaching the UI. Pass a fallback that names the action that failed.
 */
export const showToast = {
  success: (message: string, id?: string) => show('success', message, id),

  info: (message: string, id?: string) => show('info', message, id),

  /**
   * @param err      the caught error, whatever it is
   * @param fallback shown when `err` carries no user-appropriate message —
   *                 write it as "We couldn't <action>. Please try again."
   */
  error: (err: unknown, fallback: string, id?: string) =>
    show('error', getErrorMessage(err, fallback), id),

  /**
   * For a failure with no error object behind it — a precondition the UI
   * checked itself. Prefer an inline field error when the message is about
   * something the user typed or selected in a form still on screen.
   */
  errorMessage: (message: string, id?: string) => show('error', message, id),

  /**
   * Report an action that already happened and offer a way back, instead of
   * asking first. Use it only where the undo genuinely restores what was there:
   * local editor state, or an API with a real restore path. A hard DELETE with
   * no restore endpoint is not undoable, and asking first is correct there.
   */
  undo: (message: string, onUndo: () => void, id?: string) =>
    show('info', message, id, { label: 'Undo', onClick: onUndo }),

  dismiss: (id?: string) => toast.dismiss(id),
};
